import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { curry, isEqual, pick } from 'lodash-es';
import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js/max';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  IconButton,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import {
  GENERAL_ADDRESS_FIELDS,
  MEDIA,
  MY_ADDRESS_FIELDS,
  PREFERENCE_SCOPE,
  TW_ADDRESS_FIELDS,
} from '../../../../../constants/addressModule';
import { useAddressModule } from '../../../../../hooks';
import { isMY, isTW } from '../../../../../hooks/useAddressModule/utils';
import { formatPhone } from '../../../../../utils/intlTel';
import { Close as CloseIcon } from '../../../../context/Chakra/Icons';
import { useAppContext } from '../../../context/appContext';
import { addressPropTypes } from '../../../utils/propTypes';
import PhoneField from '../components/PhoneField';
import { FIELD } from '../constants/field';
import FormField from './FormField';
import Loading from './Loading';

const DEFAULT_OPTIONS = '';
const getName = (index, fieldName) =>
  `${FIELD.DELIVERY_ADDRESSES}[${index}]` + (fieldName ? `.${fieldName}` : '');
const getError = (errors, index, fieldKey) =>
  errors?.delivery_addresses?.[index]?.[fieldKey];
const hasError = (errors, index, fieldKey) =>
  Boolean(getError(errors, index, fieldKey));

const ErrorMessage = ({ fieldKey, fieldName, index }) => {
  const { errors } = useFormContext();
  const { t } = useTranslation();
  return (
    hasError(errors, index, fieldKey) && (
      <Text variant="errorMessage" textAlign="right">
        {t(getError(errors, index, fieldKey).message, {
          field_name: fieldName,
        })}
      </Text>
    )
  );
};

ErrorMessage.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

const SaveContact = (props) => {
  const { contactInfo, index, onDelete, setAddressSchemaMap } = props;
  const { merchantData, requestCountry, slFeatureService } = useAppContext();
  const hasCountryCode = slFeatureService.hasFeature('country_code');
  const {
    control,
    errors,
    formState,
    getValues,
    register,
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const curryGetName = curry(getName)(index);
  const curryHasError = curry(hasError)(errors, index);
  const recipientName = watch(curryGetName(FIELD.RECIPIENT_NAME), '');
  const address1 = watch(curryGetName(FIELD.ADDRESS_1), '');
  const validateFieldRef = useRef(null);
  const { t } = useTranslation();
  const {
    recipient_name,
    recipient_phone,
    recipient_phone_country_code,
    ...defaultAddress
  } = contactInfo;
  const recipientCountryController = useController({
    name: curryGetName(FIELD.RECIPIENT_PHONE_COUNTRY_CODE),
    control,
    defaultValue:
      recipient_phone_country_code || getCountryCallingCode(requestCountry),
  });
  const parseRecipientPhone = parsePhoneNumber(
    `+${recipientCountryController.field.value}${recipient_phone}`,
  );
  const [countryCode, setCountryCode] = useState(
    parseRecipientPhone?.country || requestCountry,
  );
  const { address, isReady, renderConfigs } = useAddressModule({
    merchantId: merchantData._id,
    scope: PREFERENCE_SCOPE.INPUT_WITH_COUNTRY,
    maxMedia: MEDIA.SMALL,
    address: { country: requestCountry, ...defaultAddress },
  });
  const addressSchema = useMemo(
    () =>
      renderConfigs.reduce((accumulate, config) => {
        accumulate[config.field_name] = yup
          .string()
          .nullable()
          .when([], {
            is: () => config.required,
            then: (schema) => schema.required('form.validation.required'),
          })
          .when([], {
            is: () => Boolean(config.rule),
            then: (schema) =>
              schema
                .required('form.validation.required')
                .matches(config.rule, 'form.validation.invalid'),
          });
        return accumulate;
      }, {}),
    [renderConfigs],
  );

  const appendToQueue = (fieldKey) => {
    validateFieldRef.current = fieldKey;
    // react hook form cannot set a registered input field to undefined; it will become an empty string.
    // Therefore, filter out falsy address data before making the API call.
    // https://github.com/react-hook-form/react-hook-form/blob/v6.15.7/src/useForm.ts#L237
    if (fieldKey === FIELD.COUNTRY) {
      Object.keys(getValues(getName(index))).forEach((key) => {
        if (
          ![
            FIELD.COUNTRY,
            FIELD.RECIPIENT_NAME,
            FIELD.RECIPIENT_PHONE,
            FIELD.RECIPIENT_PHONE_COUNTRY_CODE,
          ].includes(key)
        ) {
          setValue(getName(index, key), undefined);
        }
      });
    }
  };

  const handleValidate = (fieldKey) => {
    if (formState.isSubmitted) {
      trigger(curryGetName(fieldKey));
    }
  };

  useEffect(() => {
    if (isReady) {
      setAddressSchemaMap((prevAddressSchemaMap) => {
        if (prevAddressSchemaMap[address.country]) {
          return prevAddressSchemaMap;
        }
        return {
          ...prevAddressSchemaMap,
          [address.country]: addressSchema,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  useEffect(() => {
    // address update is asynchronous, so verification needs to wait until the address update is completed.
    if (validateFieldRef.current) {
      handleValidate(validateFieldRef.current);
      validateFieldRef.current = null;
    } else {
      handleValidate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    const country = getValues(curryGetName(FIELD.COUNTRY));
    let noneRegisterFields = GENERAL_ADDRESS_FIELDS;
    if (isTW(country)) {
      noneRegisterFields = TW_ADDRESS_FIELDS;
    } else if (isMY(country)) {
      noneRegisterFields = MY_ADDRESS_FIELDS;
    }
    const addressFormData = pick(getValues(getName(index)), noneRegisterFields);
    const addressData = pick(address, noneRegisterFields);
    if (!isEqual(addressData, addressFormData)) {
      Object.keys(addressData).forEach((fieldName) => {
        setValue(curryGetName(fieldName), address[fieldName]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const handleCountryChange = (currentNumber, { dialCode, iso2 }) => {
    const newCountry = iso2?.toLowerCase();
    if (newCountry !== countryCode.toLowerCase()) {
      setValue(curryGetName(FIELD.RECIPIENT_PHONE_COUNTRY_CODE), dialCode, {
        shouldDirty: true,
      });
      setValue(
        curryGetName(FIELD.RECIPIENT_PHONE),
        formatPhone(currentNumber, newCountry),
        {
          shouldDirty: true,
          shouldValidate: formState.isSubmitted,
        },
      );
      setCountryCode(newCountry);
    }
  };

  const handlePhoneNumberBlur = (_, phone, selectedCountryData) => {
    if (phone.length === 0 || curryHasError(FIELD.RECIPIENT_PHONE)) {
      return;
    }
    setValue(
      curryGetName(FIELD.RECIPIENT_PHONE),
      formatPhone(phone, selectedCountryData?.iso2),
    );
  };

  return (
    <AccordionItem mt={index === 0 ? '0' : '10px'}>
      <Flex mb="10px">
        <AccordionButton
          padding="0px"
          className="member-center-profile__according-button"
        >
          <AccordionIcon />
          {isReady && (
            <Text fontSize="14px" flex="1" textAlign="left" mb="0px">
              {`${recipientName} - ${address1}`}
            </Text>
          )}
        </AccordionButton>
        <IconButton
          backgroundColor="transparent"
          icon={
            <CloseIcon className="member-center-profile__delete-address-button" />
          }
          onClick={() => onDelete(index)}
          data-e2e-id={`member-center-profile-delete-contact-address_button--${index}`}
          minH="auto"
        />
      </Flex>
      <AccordionPanel py="0px" px="15px">
        <Flex direction="column" gap="1rem">
          <FormField
            label={t('checkout.address.recipient')}
            isVertical
            hasError={curryHasError(FIELD.RECIPIENT_NAME)}
          >
            <Flex direction="column" gap="0.25rem">
              <Input
                name={curryGetName(FIELD.RECIPIENT_NAME)}
                ref={register()}
                defaultValue={recipient_name}
                onChange={() => handleValidate(FIELD.RECIPIENT_NAME)}
                data-e2e-id={`member-center-profile-contact-address_name--${index}`}
              />
              <ErrorMessage
                fieldKey={FIELD.RECIPIENT_NAME}
                fieldName={t('checkout.address.recipient')}
                index={index}
              />
            </Flex>
          </FormField>
          <FormField
            label={t('checkout.address.recipient_phone')}
            isVertical
            hasError={curryHasError(FIELD.RECIPIENT_PHONE)}
          >
            <Flex direction="column" gap="0.25rem">
              {hasCountryCode ? (
                <Controller
                  name={curryGetName(FIELD.RECIPIENT_PHONE)}
                  control={control}
                  defaultValue={recipient_phone}
                  render={({ value, onChange }) => (
                    <PhoneField
                      value={value}
                      onPhoneNumberChange={(_, newPhone) => {
                        onChange(newPhone);
                        handleValidate(FIELD.RECIPIENT_PHONE);
                      }}
                      onCountryChange={handleCountryChange}
                      onPhoneNumberBlur={handlePhoneNumberBlur}
                      defaultCountry={countryCode}
                      e2eId={`member-center-profile-contact-address_phone--${index}`}
                    />
                  )}
                />
              ) : (
                <Input
                  name={curryGetName(FIELD.RECIPIENT_PHONE)}
                  ref={register()}
                  defaultValue={recipient_phone}
                  onChange={() => handleValidate(FIELD.RECIPIENT_PHONE)}
                  data-e2e-id={`member-center-profile-contact-address_phone--${index}`}
                />
              )}
              <ErrorMessage
                fieldKey={FIELD.RECIPIENT_PHONE}
                fieldName={t('checkout.address.recipient_phone')}
                index={index}
              />
            </Flex>
          </FormField>
          {!isReady ? (
            <Loading />
          ) : (
            renderConfigs.map((config) => {
              if (config.isLoading) {
                return <Loading key={config.field_name} />;
              }
              return (
                <FormField
                  key={config.field_name}
                  label={config.title}
                  isVertical
                  hasError={curryHasError(config.field_name)}
                >
                  {config.type === 'dropdown' ? (
                    <Select
                      onChange={(e) => {
                        config.onChange(e.target.value);
                        appendToQueue(config.field_name);
                      }}
                      defaultValue={config.getValue() || DEFAULT_OPTIONS}
                      isDisabled={config.isDisabled()}
                      name={curryGetName(config.field_name)}
                      ref={register({
                        setValueAs: () => address[config.field_name],
                      })}
                      data-e2e-id={`member-center-profile-contact-address_${config.field_name}--${index}`}
                    >
                      <option value={DEFAULT_OPTIONS} disabled>
                        {t('dropdown.hint')}
                      </option>
                      {config.options.map((option) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      defaultValue={address[config.field_name]}
                      onChange={(e) => {
                        config.onChange(e.target.value);
                        appendToQueue(config.field_name);
                      }}
                      placeholder={config.placeholder}
                      isDisabled={config.isDisabled(address)}
                      name={curryGetName(config.field_name)}
                      ref={register()}
                      data-e2e-id={`member-center-profile-contact-address_${config.field_name}--${index}`}
                    />
                  )}
                  <ErrorMessage
                    fieldKey={config.field_name}
                    fieldName={config.title}
                    index={index}
                  />
                </FormField>
              );
            })
          )}
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  );
};

SaveContact.propTypes = {
  contactInfo: PropTypes.PropTypes.shape({
    recipient_name: PropTypes.string,
    recipient_phone: PropTypes.string,
    recipient_phone_country_code: PropTypes.string,
    ...addressPropTypes,
  }),
  index: PropTypes.number,
  onDelete: PropTypes.func,
  setAddressSchemaMap: PropTypes.func,
};

export default SaveContact;
